import { useEffect, useRef, useState } from "react";
import { FaPlay } from "react-icons/fa";
import LazyLoadImg from "components/LazyLoadImg";

interface StickyMediaHeaderProps {
  image?: string;
  title?: string;
  subtitle?: string;
  onPlay?: () => void;
  playLabel?: string;
  sentinelRef: React.RefObject<HTMLDivElement>;
}

/**
 * Condensed "now viewing" bar that slides in once the hero's sentinel
 * scrolls out of view — the same pattern Spotify/Apple Music use so the
 * title + play action stay reachable without the full hero taking up
 * space permanently.
 */
const StickyMediaHeader = ({
  image,
  title,
  subtitle,
  onPlay,
  playLabel = "Play",
  sentinelRef,
}: StickyMediaHeaderProps) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // The hero (and its sentinel) only mount once the track/playlist data
    // has loaded, but this component mounts right away — so re-run this
    // effect once `title` actually arrives, not just once on mount, or
    // the observer would attach to a ref that was still null.
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { rootMargin: "-72px 0px 0px 0px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [sentinelRef, title]);

  return (
    <div
      className={`fixed inset-x-0 top-0 z-40 border-b border-white/10 bg-[#0b0d12]/85 backdrop-blur-xl transition-all duration-300 ${
        visible
          ? "translate-y-0 opacity-100"
          : "-translate-y-full opacity-0 pointer-events-none"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-6 py-3 max-sm:px-4 max-sm:py-2.5">
        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg ring-1 ring-white/10">
          <LazyLoadImg
            imgSrc={image}
            imgAlt={title}
            imgHeight="100%"
            imgWidth="100%"
            className="h-full w-full object-cover"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold text-white">
            {title}
          </p>
          {subtitle && (
            <p className="truncate text-[12px] text-white/50">{subtitle}</p>
          )}
        </div>
        {onPlay && (
          <button
            onClick={onPlay}
            className="button-red flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-[13px] font-semibold text-white shadow-md shadow-[var(--red)]/30 transition-transform duration-300 hover:scale-105 max-sm:px-3 max-sm:text-[12px]"
          >
            <FaPlay className="text-[10px]" />
            {playLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default StickyMediaHeader;
